import { c as _c } from "react/compiler-runtime";
import { IcoInfo24 } from '@onefootprint/icons';
import type { OnboardingRequirementCollectDocument } from '@onefootprint/request-types';
import { cx } from 'class-variance-authority';
import type React from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IDV_BODY_CONTENT_CONTAINER_ID } from '@/idv2/components/constants';
import { useLayoutOptions } from '@/idv2/components/layout/components/layout-options-provider';
import { getLogger } from '@/idv2/utils/logger';
import useProcessImage, { isProcessingResultError } from '../../hooks/use-process-image';
import type { CameraErrorCause, CaptureKind, ReceivedImagePayload } from '../../types';
import { isDesktop, isDocument, isFace } from '../../utils/capture';
import Camera, { type ChildrenProps } from '../camera';
import AutoCaptureDoc from '../camera/auto-capture-doc';
import AutoCaptureFace from '../camera/auto-capture-face';
import type { AutoCaptureKind, CaptureStatus, DeviceKind } from '../camera/types';
import type { CameraSide } from '../camera/utils/get-camera-options';
import type { NavigationHeaderLeftButtonProps } from '../navigation-header';
import NavigationHeader from '../navigation-header';
import Instructions from './components/instructions';
import Preview from './components/preview';
import useCaptureTelemetry from './use-capture-telemetry';
type HeaderTextType = {
  camera: string;
  preview: string;
};
type PhotoCaptureProps = {
  autocaptureKind: AutoCaptureKind;
  autocapture?: boolean;
  cameraKind: CameraSide;
  deviceKind: DeviceKind;
  allowPdf: boolean;
  side?: 'front' | 'back';
  sideName?: string;
  docName?: string;
  requirement: OnboardingRequirementCollectDocument;
  hasBadConnectivity?: boolean;
  onCameraStuck?: () => void;
  onCameraErrored?: (cause: CameraErrorCause) => void;
  onCameraAccessDenied?: () => void;
  onBack?: () => void;
  onComplete: (payload: ReceivedImagePayload) => void;
  outlineHeightRatio: number; // with respect to the video width (not height)
  outlineWidthRatio: number; // with respect to the video width
  subtitle?: Partial<HeaderTextType>;
  title: HeaderTextType;
};
const DesktopNavProps: NavigationHeaderLeftButtonProps = {
  variant: 'close',
  confirmClose: true
};
const {
  logWarn,
  logInfo
} = getLogger({
  name: 'idv2',
  location: 'photo-capture'
});
const getVideoCapture = ({
  config
}: OnboardingRequirementCollectDocument, autocaptureKind: AutoCaptureKind) => {
  if (config.kind === 'selfie') return config.videoCapture;
  if (config.kind === 'identity' && isFace(autocaptureKind)) return config.collectSelfieVideo;
  return undefined;
};
const SheetContent = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="absolute bottom-10 left-0 right-0 z-10"><div className="bg-primary rounded-t-2xl p-6 shadow-xl">{children}</div></div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const FallbackSheet = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="absolute bottom-0 left-0 right-0 z-10"><div className="bg-primary rounded-t-2xl p-6 shadow-xl">{children}</div></div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const PhotoCapture = t0 => {
  const $ = _c(118);
  const {
    autocaptureKind,
    autocapture: t1,
    cameraKind,
    deviceKind,
    allowPdf,
    side,
    sideName,
    docName,
    requirement,
    hasBadConnectivity,
    onCameraStuck,
    onCameraErrored,
    onCameraAccessDenied,
    onBack,
    onComplete,
    outlineHeightRatio,
    outlineWidthRatio,
    subtitle,
    title
  } = t0;
  const autocapture = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "document-flow.components.photo-capture"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t2);
  const allowUpload = requirement.uploadSettings !== "capture_only_on_mobile" && requirement.uploadSettings !== "require_mobile_capture";
  let t3;
  if ($[1] !== autocaptureKind || $[2] !== requirement) {
    t3 = getVideoCapture(requirement, autocaptureKind);
    $[1] = autocaptureKind;
    $[2] = requirement;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const videoCapture = t3;
  const recordVideo = videoCapture === "best_effort" || videoCapture === "required";
  let t4;
  if ($[4] !== allowPdf) {
    t4 = {
      allowPdf
    };
    $[4] = allowPdf;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const {
    processImageUrl
  } = useProcessImage(t4);
  const [image, setImage] = useState(null);
  const video = useRef(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [captureKind, setCaptureKind] = useState();
  const [showInstructions, setShowInstructions] = useState(false);
  const [isCaptured, setIsCaptured] = useState(false);
  const [autocaptureFeedback, setAutocaptureFeedback] = useState("detecting");
  const {
    body: t5
  } = useLayoutOptions();
  const {
    set: updateBody
  } = t5;
  let t6;
  let t7;
  if ($[6] !== deviceKind || $[7] !== updateBody) {
    t6 = () => {
      if (isDesktop(deviceKind)) {
        return;
      }
      updateBody({
        fullBleed: true
      });
      return () => updateBody({
        fullBleed: false
      });
    };
    t7 = [deviceKind, updateBody];
    $[6] = deviceKind;
    $[7] = updateBody;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useLayoutEffect(t6, t7);
  let t8;
  if ($[10] !== autocapture || $[11] !== autocaptureFeedback || $[12] !== autocaptureKind || $[13] !== side) {
    t8 = {
      autocapture,
      autocaptureKind,
      side,
      status: autocaptureFeedback
    };
    $[10] = autocapture;
    $[11] = autocaptureFeedback;
    $[12] = autocaptureKind;
    $[13] = side;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const {
    reportCapture,
    reportCameraStuck
  } = useCaptureTelemetry(t8);
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      logInfo("Retaking photo");
      setImage(null);
      video.current = undefined;
      setIsCaptured(false);
    };
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleRetake = t9;
  let t10;
  if ($[16] !== captureKind || $[17] !== hasBadConnectivity || $[18] !== image || $[19] !== onComplete || $[20] !== processImageUrl) {
    t10 = async () => {
      if (!image) {
        logWarn("Captured image could not be confirmed and submitted - retaking the image");
        return;
      }
      if (!captureKind) {
        logWarn("Captured kind could not be determined - retaking the image");
        return;
      }
      setIsLoading(true);
      const processResult = await processImageUrl(image, hasBadConnectivity);
      if (isProcessingResultError(processResult)) {
        setIsLoading(false);
        handleRetake();
        logWarn("Captured image could not be processed - retaking the image");
        return;
      }
      const videoFile = await video.current;
      setIsLoading(false);
      const {
        file,
        extraCompressed
      } = processResult;
      onComplete({
        captureKind,
        extraCompressed,
        imageFile: file,
        forcedUpload: false,
        videoFile
      });
    };
    $[16] = captureKind;
    $[17] = hasBadConnectivity;
    $[18] = image;
    $[19] = onComplete;
    $[20] = processImageUrl;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const handleConfirm = t10;
  let t11;
  if ($[22] !== onCameraErrored) {
    t11 = cause => {
      logInfo("Camera error occurred", {
        cause
      });
      onCameraErrored?.(cause);
    };
    $[22] = onCameraErrored;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const handleError = t11;
  let t12;
  if ($[24] !== reportCapture) {
    t12 = (newImage, newCaptureKind, newVideo) => {
      reportCapture(newCaptureKind);
      setImage(newImage);
      video.current = newVideo;
      setCaptureKind(newCaptureKind);
    };
    $[24] = reportCapture;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const handleCapture = t12;
  let t13;
  if ($[26] !== onComplete || $[27] !== reportCapture) {
    t13 = payload => {
      reportCapture(payload.captureKind);
      onComplete(payload);
    };
    $[26] = onComplete;
    $[27] = reportCapture;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const handleUpload = t13;
  let t14;
  if ($[29] !== onCameraStuck || $[30] !== reportCameraStuck) {
    t14 = () => {
      reportCameraStuck();
      onCameraStuck?.();
      logInfo("Photocapture: Camera stuck, fallback to doc upload");
    };
    $[29] = onCameraStuck;
    $[30] = reportCameraStuck;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const handleCameraStuck = t14;
  let t15;
  if ($[32] !== autocapture || $[33] !== autocaptureKind || $[34] !== isCaptured) {
    t15 = t16 => {
      const {
        canvasAutoCaptureRef,
        feedbackPositionFromBottom,
        videoResolution,
        onDetectionComplete,
        onDetectionReset,
        outlineHeight,
        outlineWidth,
        videoRef,
        videoSize
      } = t16;
      if (!autocapture) {
        return null;
      }
      if (isDocument(autocaptureKind)) {
        return <AutoCaptureDoc canvasAutoCaptureRef={canvasAutoCaptureRef} feedbackPositionFromBottom={feedbackPositionFromBottom} isCaptured={isCaptured} videoResolution={videoResolution} onDetectionComplete={onDetectionComplete} onDetectionReset={onDetectionReset} outlineHeight={outlineHeight} outlineWidth={outlineWidth} setAutoCaptureFeedback={setAutocaptureFeedback} videoRef={videoRef} videoSize={videoSize} />;
      }
      return isFace(autocaptureKind) ? <AutoCaptureFace canvasAutoCaptureRef={canvasAutoCaptureRef} feedbackPositionFromBottom={feedbackPositionFromBottom} isCaptured={isCaptured} onDetectionComplete={onDetectionComplete} onDetectionReset={onDetectionReset} outlineWidth={outlineWidth} setAutoCaptureFeedback={setAutocaptureFeedback} videoRef={videoRef} videoSize={videoSize} videoResolution={videoResolution} /> : null;
    };
    $[32] = autocapture;
    $[33] = autocaptureKind;
    $[34] = isCaptured;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const renderCameraChildren = t15;
  let t16;
  if ($[36] !== deviceKind) {
    t16 = isDesktop(deviceKind);
    $[36] = deviceKind;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const desktop = t16;
  const showOverlay = !!(image || isLoading);
  let t17;
  let t18;
  if ($[38] !== showOverlay) {
    t17 = () => {
      if (!showOverlay) {
        return;
      }
      const target = document.getElementById(IDV_BODY_CONTENT_CONTAINER_ID)?.parentElement;
      if (target && getComputedStyle(target).position === "static") {
        target.style.position = "relative";
      }
    };
    t18 = [showOverlay];
    $[38] = showOverlay;
    $[39] = t17;
    $[40] = t18;
  } else {
    t17 = $[39];
    t18 = $[40];
  }
  useEffect(t17, t18);
  let t19;
  let t20;
  let t21;
  let t22;
  if ($[41] !== allowPdf || $[42] !== allowUpload || $[43] !== autocapture || $[44] !== autocaptureFeedback || $[45] !== autocaptureKind || $[46] !== cameraKind || $[47] !== desktop || $[48] !== deviceKind || $[49] !== docName || $[50] !== handleCameraStuck || $[51] !== handleCapture || $[52] !== handleConfirm || $[53] !== handleError || $[54] !== handleUpload || $[55] !== hasBadConnectivity || $[56] !== image || $[57] !== isCaptured || $[58] !== isLoading || $[59] !== onBack || $[60] !== onCameraAccessDenied || $[61] !== outlineHeightRatio || $[62] !== outlineWidthRatio || $[63] !== recordVideo || $[64] !== renderCameraChildren || $[65] !== showOverlay || $[66] !== sideName || $[67] !== subtitle || $[68] !== t || $[69] !== title.camera) {
    const sheetPortalTarget = showOverlay ? document.getElementById(IDV_BODY_CONTENT_CONTAINER_ID)?.parentElement ?? null : null;
    if ($[74] !== desktop || $[75] !== onBack || $[76] !== subtitle || $[77] !== title.camera) {
      t22 = desktop ? <div className="mb-6"><NavigationHeader leftButton={DesktopNavProps} /><div className="flex flex-col items-center gap-2 text-center"><h2 className="text-heading-3 text-primary">{title.camera}</h2>{subtitle?.camera && <p className="text-body-2 text-secondary">{subtitle.camera}</p>}</div></div> : <NavigationHeader leftButton={{
        variant: "back",
        onBack
      }} rightButton={{
        icon: IcoInfo24,
        onClick: () => setShowInstructions(true)
      }} content={{
        kind: "static",
        title: title.camera
      }} />;
      $[74] = desktop;
      $[75] = onBack;
      $[76] = subtitle;
      $[77] = title.camera;
      $[78] = t22;
    } else {
      t22 = $[78];
    }
    t19 = "relative w-full flex-1 min-h-0";
    const t23 = showOverlay && "blur-sm pointer-events-none";
    let t24;
    if ($[79] !== t23) {
      t24 = cx("w-full h-full transition-[filter]", t23);
      $[79] = t23;
      $[80] = t24;
    } else {
      t24 = $[80];
    }
    let t25;
    if ($[81] !== allowPdf || $[82] !== allowUpload || $[83] !== autocapture || $[84] !== autocaptureFeedback || $[85] !== autocaptureKind || $[86] !== cameraKind || $[87] !== deviceKind || $[88] !== docName || $[89] !== handleCameraStuck || $[90] !== handleCapture || $[91] !== handleError || $[92] !== handleUpload || $[93] !== hasBadConnectivity || $[94] !== isCaptured || $[95] !== onCameraAccessDenied || $[96] !== outlineHeightRatio || $[97] !== outlineWidthRatio || $[98] !== recordVideo || $[99] !== renderCameraChildren || $[100] !== sideName) {
      t25 = <Camera autocapture={autocapture} autoCaptureFeedback={autocaptureFeedback} autoCaptureKind={autocaptureKind} cameraSide={cameraKind} deviceKind={deviceKind} docName={docName} sideName={sideName} onCapture={handleCapture} onUpload={handleUpload} recordVideo={recordVideo} hasBadConnectivity={hasBadConnectivity} onCameraError={handleError} onCameraAccessDenied={onCameraAccessDenied} outlineHeightRatio={outlineHeightRatio} outlineWidthRatio={outlineWidthRatio} isCaptured={isCaptured} setIsCaptured={setIsCaptured} allowPdf={allowPdf} onCameraStuck={handleCameraStuck} allowUpload={allowUpload}>{renderCameraChildren}</Camera>;
      $[81] = allowPdf;
      $[82] = allowUpload;
      $[83] = autocapture;
      $[84] = autocaptureFeedback;
      $[85] = autocaptureKind;
      $[86] = cameraKind;
      $[87] = deviceKind;
      $[88] = docName;
      $[89] = handleCameraStuck;
      $[90] = handleCapture;
      $[91] = handleError;
      $[92] = handleUpload;
      $[93] = hasBadConnectivity;
      $[94] = isCaptured;
      $[95] = onCameraAccessDenied;
      $[96] = outlineHeightRatio;
      $[97] = outlineWidthRatio;
      $[98] = recordVideo;
      $[99] = renderCameraChildren;
      $[100] = sideName;
      $[101] = t25;
    } else {
      t25 = $[101];
    }
    if ($[102] !== t24 || $[103] !== t25) {
      t20 = <div className={t24}>{t25}</div>;
      $[102] = t24;
      $[103] = t25;
      $[104] = t20;
    } else {
      t20 = $[104];
    }
    t21 = image && (sheetPortalTarget ? createPortal(<SheetContent><Preview imageSrc={image} onRetake={handleRetake} onConfirm={handleConfirm} isLoading={isLoading} cameraKind={cameraKind} deviceKind={deviceKind} title={isDocument(autocaptureKind) ? t(_temp) : t(_temp2)} /></SheetContent>, sheetPortalTarget) : <FallbackSheet><Preview imageSrc={image} onRetake={handleRetake} onConfirm={handleConfirm} isLoading={isLoading} cameraKind={cameraKind} deviceKind={deviceKind} title={isDocument(autocaptureKind) ? t(_temp3) : t(_temp4)} /></FallbackSheet>);
    $[41] = allowPdf;
    $[42] = allowUpload;
    $[43] = autocapture;
    $[44] = autocaptureFeedback;
    $[45] = autocaptureKind;
    $[46] = cameraKind;
    $[47] = desktop;
    $[48] = deviceKind;
    $[49] = docName;
    $[50] = handleCameraStuck;
    $[51] = handleCapture;
    $[52] = handleConfirm;
    $[53] = handleError;
    $[54] = handleUpload;
    $[55] = hasBadConnectivity;
    $[56] = image;
    $[57] = isCaptured;
    $[58] = isLoading;
    $[59] = onBack;
    $[60] = onCameraAccessDenied;
    $[61] = outlineHeightRatio;
    $[62] = outlineWidthRatio;
    $[63] = recordVideo;
    $[64] = renderCameraChildren;
    $[65] = showOverlay;
    $[66] = sideName;
    $[67] = subtitle;
    $[68] = t;
    $[69] = title.camera;
    $[70] = t19;
    $[71] = t20;
    $[72] = t21;
    $[73] = t22;
  } else {
    t19 = $[70];
    t20 = $[71];
    t21 = $[72];
    t22 = $[73];
  }
  let t23;
  if ($[105] !== t19 || $[106] !== t20 || $[107] !== t21) {
    t23 = <div className={t19}>{t20}{t21}</div>;
    $[105] = t19;
    $[106] = t20;
    $[107] = t21;
    $[108] = t23;
  } else {
    t23 = $[108];
  }
  let t24;
  if ($[109] !== autocapture || $[110] !== autocaptureKind || $[111] !== desktop || $[112] !== showInstructions) {
    t24 = !desktop && <Instructions onClose={() => setShowInstructions(false)} isOpen={showInstructions} autoCaptureKind={autocaptureKind} autocapture={autocapture} />;
    $[109] = autocapture;
    $[110] = autocaptureKind;
    $[111] = desktop;
    $[112] = showInstructions;
    $[113] = t24;
  } else {
    t24 = $[113];
  }
  let t25;
  if ($[114] !== t22 || $[115] !== t23 || $[116] !== t24) {
    t25 = <>{t22}{t23}{t24}</>;
    $[114] = t22;
    $[115] = t23;
    $[116] = t24;
    $[117] = t25;
  } else {
    t25 = $[117];
  }
  return t25;
};
export default PhotoCapture;
function _temp($) {
  return $.preview.titleDocument;
}
function _temp2($_0) {
  return $_0.preview.titleFace;
}
function _temp3($_1) {
  return $_1.preview.titleDocument;
}
function _temp4($_2) {
  return $_2.preview.titleFace;
}